import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { QuoteProvider } from "@/context/QuoteContext";
import { LanguageProvider } from "@/i18n/LanguageContext";
import PublicLayout from "@/components/PublicLayout";
import Home from "@/pages/Home";
import Compare from "@/pages/Compare";
import ClinicDetail from "@/pages/ClinicDetail";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import TripJourney from "@/pages/TripJourney";
import Transformations from "@/pages/Transformations";
import VideoTestimonials from "@/pages/VideoTestimonials";
import AdLanding from "@/pages/AdLanding";
import LocationsIndex from "@/pages/LocationsIndex";
import LocationDetail from "@/pages/LocationDetail";
import Stories from "@/pages/Stories";
import ShareStory from "@/pages/ShareStory";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import ClinicsAdmin from "@/pages/admin/ClinicsAdmin";
import LeadsAdmin from "@/pages/admin/LeadsAdmin";
import ContentAdmin from "@/pages/admin/ContentAdmin";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType(); // POP (back/forward) | PUSH | REPLACE
  useEffect(() => {
    // On back/forward, let the browser restore the previous scroll position
    // (so returning from a clinic lands you back on the clinics grid).
    if (navType !== "POP") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [pathname, navType]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <LanguageProvider>
        <QuoteProvider>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/clinics" element={<Compare />} />
              <Route path="/clinics/:id" element={<ClinicDetail />} />
              <Route path="/locations" element={<LocationsIndex />} />
              <Route path="/locations/:slug" element={<LocationDetail />} />
              <Route path="/journey" element={<TripJourney />} />
              <Route path="/transformations" element={<Transformations />} />
              <Route path="/video-testimonials" element={<VideoTestimonials />} />
              <Route path="/lp/implants" element={<AdLanding variant="implants" />} />
              <Route path="/lp/all-on-4" element={<AdLanding variant="all-on-4" />} />
              <Route path="/lp/veneers-crowns" element={<AdLanding variant="veneers-crowns" />} />
              <Route path="/stories" element={<Stories />} />
              <Route path="/share-your-story" element={<ShareStory />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="leads" element={<LeadsAdmin />} />
              <Route path="clinics" element={<ClinicsAdmin />} />
              <Route path="content" element={<ContentAdmin />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </QuoteProvider>
        </LanguageProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
